





















import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import AgroGenericModal from "@/components/agroGenericModal/AgroGenericModal.vue";
import AgroIcons from "@/components/agroIcons/AgroIcons.vue";
import i18n from "@/i18n";
import { createEmbedSession, SETTE_MAPS_VIEWER_URL } from "@/api/sette-maps";

// Formats the viewer sends in its ready message (packages/contracts of ativus/sette-maps).
const NONCE = /^[A-Za-z0-9._~-]{16,128}$/;
const CODE_CHALLENGE = /^[A-Za-z0-9_-]{43,128}$/;
// Without a ready message by then the viewer did not load (network, viewer down, or a page that
// may not frame it): say so instead of "opening" forever.
const READY_TIMEOUT_MS = 20_000;

// Host side of the Sette Maps embed protocol (channel "sette-maps", version "1"):
// 1. the viewer posts sette-maps:ready with a nonce and its PKCE challenge;
// 2. the host creates a session with the user's token and gets a one-time code;
// 3. the host posts sette-maps:authorize with that code; the viewer exchanges it itself.
@Component({
  name: "SetteMapsViewerModal",
  components: {
    AgroGenericModal,
    icon: AgroIcons,
  },
})
export default class SetteMapsViewerModal extends Vue {
  @Prop({ type: Boolean, default: false }) private isVisible!: boolean;
  @Prop({ type: String, required: true }) private resourceRef!: string;

  private status = "";
  private isError = false;
  private activeNonce: string | null = null;
  private controller: AbortController | null = null;
  private readyTimer: number | null = null;

  private get viewerUrl(): string {
    return SETTE_MAPS_VIEWER_URL;
  }

  private get viewerOrigin(): string {
    return new URL(SETTE_MAPS_VIEWER_URL).origin;
  }

  @Watch("isVisible", { immediate: true })
  private onVisibilityChange(visible: boolean): void {
    this.cancelAuthorization();
    this.clearReadyTimer();
    window.removeEventListener("message", this.onMessage);
    if (visible) {
      this.setStatus(i18n.t("monitoring.sette-maps.loading") as string);
      window.addEventListener("message", this.onMessage);
      this.readyTimer = window.setTimeout(() => {
        this.readyTimer = null;
        this.setStatus(i18n.t("monitoring.sette-maps.unreachable") as string, true);
      }, READY_TIMEOUT_MS);
      // Focus the modal so Esc closes it (keys typed inside the iframe never reach this page).
      this.$nextTick(() => (this.$el as HTMLElement).focus());
    }
  }

  private beforeDestroy(): void {
    this.cancelAuthorization();
    this.clearReadyTimer();
    window.removeEventListener("message", this.onMessage);
  }

  private clearReadyTimer(): void {
    if (this.readyTimer !== null) {
      window.clearTimeout(this.readyTimer);
      this.readyTimer = null;
    }
  }

  private setStatus(message: string, isError = false): void {
    this.status = message;
    this.isError = isError;
  }

  private cancelAuthorization(): void {
    if (this.controller) {
      this.controller.abort();
    }
    this.controller = null;
    this.activeNonce = null;
  }

  private onMessage(event: MessageEvent): void {
    const frame = this.$refs.frame as HTMLIFrameElement | undefined;
    if (!frame || event.origin !== this.viewerOrigin || event.source !== frame.contentWindow) {
      return;
    }
    const message = event.data;
    if (!message || message.channel !== "sette-maps" || message.version !== "1") {
      return;
    }

    if (message.type === "sette-maps:ready") {
      // The same ready twice is ignored: authorizing again would create a second session.
      if (NONCE.test(message.nonce) && CODE_CHALLENGE.test(message.codeChallenge) && message.nonce !== this.activeNonce) {
        this.clearReadyTimer();
        this.authorize(message.nonce, message.codeChallenge, frame);
      }
    } else if (message.nonce !== this.activeNonce) {
      return;
    } else if (message.type === "sette-maps:loaded") {
      this.setStatus("");
    } else if (message.type === "sette-maps:error") {
      const text = message.error && message.error.message;
      this.setStatus(text || (i18n.t("monitoring.sette-maps.error") as string), true);
      if (message.error && message.error.code === "authorization_failed") {
        this.activeNonce = null;
      }
    }
  }

  private async authorize(nonce: string, codeChallenge: string, frame: HTMLIFrameElement): Promise<void> {
    // A new ready message (the viewer reloaded) replaces an authorization still in progress.
    this.cancelAuthorization();
    const controller = new AbortController();
    this.controller = controller;
    this.activeNonce = nonce;
    this.setStatus(i18n.t("monitoring.sette-maps.authorizing") as string);
    try {
      const code = await createEmbedSession(
        this.$store.state.auth.accessToken,
        { resourceRef: this.resourceRef, parentOrigin: window.location.origin, nonce, codeChallenge },
        controller.signal
      );
      if (this.controller !== controller || this.activeNonce !== nonce || !frame.contentWindow) {
        return;
      }
      frame.contentWindow.postMessage(
        { channel: "sette-maps", version: "1", type: "sette-maps:authorize", nonce, code },
        this.viewerOrigin
      );
    } catch (error) {
      if (this.controller !== controller) {
        return;
      }
      this.activeNonce = null;
      const reason = error instanceof Error ? error.message : "";
      this.setStatus(`${i18n.t("monitoring.sette-maps.error")}${reason ? `: ${reason}` : ""}`, true);
    } finally {
      if (this.controller === controller) {
        this.controller = null;
      }
    }
  }

  @Emit("close")
  private close(): void {
    // The parent hides the modal; the watcher then drops the iframe and the listener.
  }
}
